import {
  Prisma,
  prisma,
  ResourceType,
  ActionType,
  IntegrationType,
} from "client";

type SettingsType =
  | {
      apiKey: string;
      apiEndpoint: string;
      clientId: string;
      grantType: string;
      scope: string;
      clientSecret: string;
      channel: string;
      sellerCode: string;
    }
  | {
      siteKey: string;
      secretKey: string;
    };

// CREATE
export const createIntegration = async (
  userId: string,
  data: {
    whiteLabelId?: string;
    organizationId?: string;
    type: IntegrationType;
    settings: SettingsType;
    isActive?: boolean;
    orgCanConfigure?: boolean;
  }
) => {
  try {
    const integration = await prisma.integration.create({
      data: {
        ...(data.organizationId
          ? { organizationId: data.organizationId, whiteLabelId: null }
          : { whiteLabelId: data.whiteLabelId!, organizationId: null }),
        type: data.type,
        settings: data.settings,
        isActive: data.isActive ?? true,
        orgCanConfigure: data.orgCanConfigure ?? false,
      },
    });

    return { integration };
  } catch (error) {
    console.log("error", error);
    return { error };
  }
};

// READ
export const getAllIntegrations = async (
  userId: string,
  whiteLabelId?: string
) => {
  try {
    const whereClause: any = {};
    if (whiteLabelId) {
      whereClause.whiteLabelId = whiteLabelId;
    }

    const integrations = await prisma.integration.findMany({
      where: {
        ...whereClause,
        isActive: true,
      },
      include: {
        whiteLabel: true,
      },
    });

    return { integrations };
  } catch (error) {
    return { error };
  }
};

export const getIntegrationById = async (userId: string, id: string) => {
  try {
    const integration = await prisma.integration.findUnique({
      where: { id },
      include: {
        whiteLabel: true,
      },
    });

    return { integration };
  } catch (error) {
    return { error };
  }
};

export const getIntegrationsByWhiteLabelId = async (
  userId: string,
  whiteLabelId: string
) => {
  try {
    const integrations = await prisma.integration.findMany({
      where: {
        whiteLabelId,
      },
      include: {
        whiteLabel: true,
      },
    });

    return { integrations };
  } catch (error) {
    return { error };
  }
};

export const getIntegrationsByOrganizationId = async (
  userId: string,
  organizationId: string
) => {
  try {
    const integrations = await prisma.integration.findMany({
      where: {
        organizationId,
      },
      include: {
        organization: true,
      },
    });

    return { integrations };
  } catch (error) {
    return { error };
  }
};

export const getIntegrationByOrganizationIdAndType = async (
  type: IntegrationType,
  organizationId?: string,
  eventId?: string
) => {
  try {
    const integrations = await prisma.integration.findFirst({
      where: {
        type,
        ...(eventId
          ? {
              organization: {
                events: {
                  some: {
                    id: eventId,
                  },
                },
              },
            }
          : { organizationId }),
      },
    });

    return { integrations };
  } catch (error) {
    return { error };
  }
};

export const getIntegrationByType = async (userId: string, type: "DTCM") => {
  try {
    const integration = await prisma.integration.findFirst({
      where: {
        type,
        isActive: true,
      },
      include: {
        whiteLabel: true,
      },
    });

    return { integration };
  } catch (error) {
    return { error };
  }
};

// UPDATE
export const updateIntegrationById = async (
  userId: string,
  id: string,
  data: {
    whiteLabelId?: string;
    type?: "DTCM";
    settings?: SettingsType;
    isActive?: boolean;
    orgCanConfigure?: boolean;
  }
) => {
  try {
    const current = await prisma.integration.findUnique({
      where: { id },
    });

    if (!current) {
      throw new Error("Integration not found");
    }

    const mergedData: any = { ...data };

    if (data.settings) {
      mergedData.settings = {
        ...(current.settings as any),
        ...data.settings,
      };
    }

    const integration = await prisma.integration.update({
      where: { id },
      data: mergedData,
      include: {
        whiteLabel: true,
      },
    });

    return { integration };
  } catch (error) {
    return { error };
  }
};

export const activateDeactivateIntegrationById = async (
  userId: string,
  id: string
) => {
  try {
    const currentIntegration = await prisma.integration.findUnique({
      where: { id },
    });

    if (!currentIntegration) {
      throw new Error("Integration not found");
    }

    const integration = await prisma.integration.update({
      where: { id },
      data: {
        isActive: !currentIntegration.isActive,
      },
      include: {
        whiteLabel: true,
      },
    });

    return { integration };
  } catch (error) {
    return { error };
  }
};

// DELETE
export const deleteIntegrationById = async (userId: string, id: string) => {
  try {
    const integration = await prisma.integration.delete({
      where: { id },
    });

    return { integration };
  } catch (error) {
    return { error };
  }
};

// Helper Functions
export const validateIntegrationSettings = async (
  type: "DTCM",
  settings: any
) => {
  try {
    switch (type) {
      case "DTCM":
        // Add validation for DTCM settings
        if (!settings.apiKey || !settings.apiEndpoint) {
          throw new Error("Invalid DTCM settings: Missing required fields");
        }
        break;
      // Add more cases for future integration types
      default:
        throw new Error("Unsupported integration type");
    }
    return true;
  } catch (error) {
    return { error };
  }
};
